/** The Ledger tab's words (`ledger.json`, Zod-validated; W4 card C4). */
import { z } from 'zod'
import ledgerJson from './ledger.json'
import { LEASE_EVENT_KINDS } from '../../../../career/types'

const text = z.string().min(1)
const tone = z.enum(['danger', 'warn', 'neutral', 'ok'])

const kinds = z.strictObject(Object.fromEntries(LEASE_EVENT_KINDS.map((k) => [k, text])) as Record<(typeof LEASE_EVENT_KINDS)[number], typeof text>)

export const ledgerConfigSchema = z.strictObject({
  comment: z.string(),
  order: z.strictObject({ finances: z.number(), standing: z.number(), law: z.number(), corporation: z.number() }),
  sections: z.strictObject({ finances: text, standing: text, law: text, corporation: text }),
  historyRows: z.number().int().positive(),
  /** Worker reads (captainView, corpView) per second while open. */
  asyncHz: z.number().positive().max(4),
  noCareer: text,
  finances: z.strictObject({
    wallet: text,
    principal: text,
    owned: text,
    nextDue: text,
    nextDueValue: text,
    arrears: text,
    collections: text,
    collectionsState: z.strictObject({ none: text, notice: text, repo: text }),
    insurerDebt: text,
    wageBill: text,
    wageBillValue: text,
    wageBillNone: text,
    history: text,
    historyEmpty: text,
    historyAgo: text,
    dueNow: text,
    dueIn: text,
    kinds,
    payLease: text,
  }),
  standing: z.strictObject({
    columns: z.strictObject({ faction: text, value: text, tier: text, last: text }),
    tiers: z.array(z.strictObject({ min: z.number(), label: text, tone })).min(1),
    lastChange: text,
    noChange: text,
  }),
  law: z.strictObject({
    jurisdiction: text,
    lawless: text,
    unknown: text,
    corpClaim: text,
    claim: text,
    status: text,
    clean: text,
    wanted: text,
    columns: z.strictObject({ authority: text, bounty: text, wanted: text }),
    noBounties: text,
    finesOwed: text,
    payFines: text,
    payFinesReason: text,
    payFinesWhere: text,
  }),
  corporation: z.strictObject({
    notFounded: text,
    foundingNeeds: text,
    found: text,
    foundReason: text,
    foundWhere: text,
    name: text,
    foundedDay: text,
    foundedDayValue: text,
    treasury: text,
    net: text,
    netValue: text,
    income: text,
    costs: text,
    pnlValue: text,
    claims: text,
    claimValue: text,
    noClaims: text,
    milestones: text,
    milestoneDone: text,
    milestoneOpen: text,
    loading: text,
  }),
})

export type LedgerConfig = z.infer<typeof ledgerConfigSchema>
export type Tone = z.infer<typeof tone>

let cached: LedgerConfig | null = null

export function loadLedgerConfig(): LedgerConfig {
  if (cached !== null) return cached
  const c = ledgerConfigSchema.parse(ledgerJson)
  for (let i = 1; i < c.standing.tiers.length; i++) {
    if (c.standing.tiers[i].min <= c.standing.tiers[i - 1].min) throw new Error('ledger.json: standing.tiers must rise by min')
  }
  cached = c
  return c
}
